/**
 * One builder for every alert: on an instrument, on a watchlist, or on the portfolio. Opened from the
 * Markets rows, the instrument quick view, the trade screen, Portfolio and Account → Alerts. The economic
 * calendar opens it for a release reminder (`event`) and for key economic events (`key_events`).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ALERT_KIND_LABEL,
  ECON_CATEGORIES,
  ECON_CURRENCIES,
  ECON_GUIDE_BY_SLUG,
  EVENT_REMIND_MINUTES,
  pairLabel,
  searchInstruments,
  type AlertChannel,
  type AlertInput,
  type AlertKind,
  type AlertRepeat,
} from '@platform/shared';
import { ApiError } from '../lib/api';
import { closeAlertBuilder, createAlert, pushSubscribed, updateAlert, useAlertBuilder } from '../stores/alerts';
import { useInstrument, useInstruments, useTicker } from '../stores/market';
import { useWatchlists, loadWatchlists } from '../stores/markets';
import { useActiveAccount } from '../stores/session';
import { Link } from '../lib/router';
import { InstrumentIcon } from './InstrumentIcon';
import { PairName } from './PairName';
import { cx, Icon, Modal, Spinner } from './ui';

type Scope = 'asset' | 'watchlist' | 'portfolio';

const SCOPE_KINDS: Record<Scope, AlertKind[]> = {
  asset: ['price_above', 'price_below', 'move', 'range_break', 'market_open'],
  watchlist: ['watchlist_move'],
  portfolio: ['position_pnl', 'tp_sl_near', 'equity_drawdown', 'margin_level', 'concentration', 'daily_summary'],
};

const scopeOf = (k: AlertKind): Scope => (SCOPE_KINDS.portfolio.includes(k) ? 'portfolio' : k === 'watchlist_move' ? 'watchlist' : 'asset');

const inputCls = 'h-[42px] w-full rounded-lg border bg-bg px-3 text-[14.5px] text-fg outline-none placeholder:text-muted hover:border-muted focus:border-muted';
const fieldBorder = (bad: boolean) => (bad ? 'border-down' : 'border-line-strong');
const chipCls = (on: boolean) =>
  cx('h-[38px] rounded-md border px-3.5 text-[14px] font-medium whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-40', on ? 'border-muted bg-raised text-fg' : 'border-line-strong text-muted hover:border-muted hover:text-fg');

function Field({ label, children, className, htmlFor, aside }: { label: string; children: ReactNode; className?: string; htmlFor?: string; aside?: ReactNode }) {
  return (
    <div className={cx('flex min-w-0 flex-col gap-2', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={htmlFor} className="text-[13.5px] font-medium text-muted">
          {label}
        </label>
        {aside}
      </div>
      {children}
    </div>
  );
}

/** A row of outlined buttons, one of which is on. */
function Chips<T extends string>({ value, options, onChange, label, id }: { value: T; options: { id: T; label: string; disabled?: boolean }[]; onChange: (v: T) => void; label: string; id?: string }) {
  return (
    <div id={id} role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o.id} type="button" role="radio" aria-checked={value === o.id} disabled={o.disabled} onClick={() => onChange(o.id)} className={chipCls(value === o.id)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Outlined buttons, any number of which are on. */
function MultiChips<T extends string>({ value, options, onChange, label }: { value: T[]; options: { id: T; label: string }[]; onChange: (v: T[]) => void; label: string }) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = value.includes(o.id);
        return (
          <button key={o.id} type="button" aria-pressed={on} onClick={() => onChange(on ? value.filter((x) => x !== o.id) : [...value, o.id])} className={chipCls(on)}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Help({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'down' | 'warn' }) {
  return <span className={cx('text-[12.5px]', tone === 'down' ? 'text-down' : tone === 'warn' ? 'text-warn' : 'text-muted')}>{children}</span>;
}

/** The chosen instrument as a card (logo, name, price); tapping it opens a search with keyboard support. */
function InstrumentPicker({ symbol, onPick }: { symbol: string | null; onPick: (symbol: string) => void }) {
  const instruments = useInstruments();
  const inst = useInstrument(symbol ?? '');
  const ticker = useTicker(symbol ?? '');
  const [open, setOpen] = useState(!symbol);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rows = useMemo(
    () => (query.trim() ? searchInstruments(instruments, query).map((h) => h.item) : [...instruments].sort((a, b) => a.symbol.localeCompare(b.symbol))).slice(0, 6),
    [instruments, query],
  );
  const close = () => {
    setOpen(false);
    setQuery('');
  };
  const pick = (s: string) => {
    onPick(s);
    close();
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(rows.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rows[active]) pick(rows[active].symbol);
    } else if (e.key === 'Escape' && symbol) {
      // Closes the search only, not the whole builder.
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };

  if (!open && symbol) {
    return (
      <button
        id="alert-symbol"
        type="button"
        onClick={() => {
          setActive(0);
          setOpen(true);
        }}
        aria-label={`Instrument ${pairLabel(symbol)}${inst ? `, ${inst.name}` : ''}. Change instrument`}
        className="flex h-12 w-full items-center gap-3 rounded-lg border border-line-strong bg-bg px-2.5 text-left hover:border-muted"
      >
        <InstrumentIcon symbol={symbol} size={30} />
        <span className="flex min-w-0 flex-1 flex-col">
          <PairName symbol={symbol} assetClass={inst?.assetClass} className="text-[15px] font-semibold" />
          <span className="truncate text-[12.5px] text-muted">{inst?.name}</span>
        </span>
        <span className="flex shrink-0 flex-col items-end">
          {ticker && <span className="num text-[14px] font-medium">{ticker.mark}</span>}
          <span className="text-[12px] text-muted">Change</span>
        </span>
      </button>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Icon name="search" size={17} className="pointer-events-none absolute top-[12.5px] left-3 text-muted" />
        <input
          id="alert-symbol"
          autoFocus
          role="combobox"
          aria-expanded="true"
          aria-controls="alert-symbol-list"
          aria-activedescendant={rows[active] ? `alert-symbol-${active}` : undefined}
          aria-autocomplete="list"
          autoComplete="off"
          spellCheck={false}
          placeholder="Search instrument or company"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => symbol && close()}
          className={cx(inputCls, 'border-muted pr-10 pl-10 font-medium')}
        />
        {query && (
          <button type="button" aria-label="Clear search" onMouseDown={(e) => { e.preventDefault(); setQuery(''); setActive(0); }} className="absolute top-[7px] right-[7px] grid size-7 place-items-center rounded-md text-muted hover:bg-raised hover:text-fg">
            <Icon name="close" size={14} />
          </button>
        )}
      </div>
      <div id="alert-symbol-list" role="listbox" aria-label="Instruments" className="shadow-pop absolute inset-x-0 top-[calc(100%+6px)] z-10 rounded-lg border border-line-strong bg-panel py-1">
        {rows.map((i, n) => (
          <button
            key={i.symbol}
            id={`alert-symbol-${n}`}
            type="button"
            role="option"
            tabIndex={-1}
            aria-selected={n === active}
            onMouseDown={(e) => {
              e.preventDefault();
              pick(i.symbol);
            }}
            onMouseEnter={() => setActive(n)}
            className={cx('flex h-11 w-full items-center gap-3 px-3 text-left', n === active && 'bg-raised')}
          >
            <InstrumentIcon symbol={i.symbol} size={26} />
            <PairName symbol={i.symbol} assetClass={i.assetClass} query={query} tag={false} className="w-[84px] shrink-0 text-[14.5px] font-semibold" />
            <span className="min-w-0 truncate text-[13.5px] text-muted">{i.name}</span>
          </button>
        ))}
        {!rows.length && <p className="m-0 px-3 py-3 text-[13.5px] text-muted">No instrument matches “{query}”</p>}
        <div aria-hidden="true" className="mt-1 flex gap-3 border-t border-line px-3 pt-2 pb-1.5 text-[11.5px] text-muted max-sm:hidden">
          <span>↑ ↓ move</span>
          <span>Enter select</span>
          {symbol && <span>Esc close</span>}
        </div>
      </div>
    </div>
  );
}

const roundTo = (x: number, dp: number) => Number(x.toFixed(dp));

/** Sentence describing what the alert does, shown under the fields. */
function describe(kind: AlertKind, symbol: string | null, p: Record<string, any>, watchlistName: string | undefined): string {
  const s = symbol ? pairLabel(symbol) : '';
  switch (kind) {
    case 'price_above':
      return `When the ${s} price rises to ${p.price || '…'} or above.`;
    case 'price_below':
      return `When the ${s} price falls to ${p.price || '…'} or below.`;
    case 'move':
      return `When ${s} ${p.direction === 'up' ? 'rises' : p.direction === 'down' ? 'falls' : 'moves'} ${p.pct || '…'}% or more within ${p.window === '24h' ? '24 hours' : 'an hour'}.`;
    case 'range_break':
      return `When ${s} trades ${p.side === 'high' ? 'above its previous' : 'below its previous'} ${p.window} ${p.side}.`;
    case 'market_open':
      return `When the ${s} market opens for trading.`;
    case 'event':
      return `${p.minutesBefore ?? 15} minutes before ${p.every ? 'every release of ' : ''}${p.title ?? 'the event'}${p.result ? ', then the result as soon as it is published' : ''}.`;
    case 'key_events': {
      const cats = (p.categories ?? []).map((c: string) => ECON_CATEGORIES.find((x) => x.id === c)?.label.toLowerCase()).filter(Boolean);
      const what = [p.high ? 'every high-impact release' : null, cats.length ? `every ${cats.join(', ')} release` : null].filter(Boolean).join(' and ');
      const where = p.currencies?.length ? ` for ${p.currencies.join(', ')}` : '';
      return `${p.minutesBefore ?? 15} minutes before ${what || '…'}${where}${p.result ? ', then each result we have' : ''}. Releases at the same time come in one notice.`;
    }
    case 'watchlist_move':
      return `When any instrument in ${watchlistName ?? 'the watchlist'} moves ${p.pct || '…'}% or more within ${p.window === '24h' ? '24 hours' : 'an hour'}.`;
    case 'position_pnl':
      return `When ${p.positionId ? 'this position' : symbol ? `any ${s} position` : 'any open position'} is ${p.direction === 'loss' ? 'down' : 'up'} $${p.usd || '…'} or more, including swap.`;
    case 'tp_sl_near':
      return `When the price comes within ${p.pct || '…'}% of the take profit or stop loss on ${p.positionId ? 'this position' : 'any open position'}.`;
    case 'equity_drawdown':
      return `When account equity falls ${p.pct || '…'}% below its high for the day (UTC).`;
    case 'margin_level':
      return `When the account margin level drops to ${p.pct || '…'}% or below.`;
    case 'concentration':
      return `When one instrument is more than ${p.pct || '…'}% of your open positions.`;
    case 'daily_summary':
      return `Every day at ${String(p.hourUtc ?? 21).padStart(2, '0')}:00 UTC: P&L, swap and open positions.`;
  }
}

const DEFAULT_PARAMS: Partial<Record<AlertKind, Record<string, unknown>>> = {
  move: { pct: 3, window: '1h', direction: 'any' },
  range_break: { window: '24h', side: 'high' },
  watchlist_move: { pct: 3, window: '1h' },
  position_pnl: { usd: 100, direction: 'gain' },
  tp_sl_near: { pct: 0.5 },
  equity_drawdown: { pct: 5 },
  margin_level: { pct: 150 },
  concentration: { pct: 50 },
  daily_summary: { hourUtc: 21 },
  key_events: { high: true, categories: ['energy'], currencies: [], minutesBefore: 15, result: true },
};

const REPEAT: { id: AlertRepeat; label: string; hint: string }[] = [
  { id: 'once', label: 'Once', hint: 'Fires once, then switches off.' },
  { id: 'always', label: 'Every time', hint: 'Fires every time it happens, at most once an hour.' },
  { id: 'daily', label: 'Once a day', hint: 'Fires at most once a day.' },
];

const WINDOW = [
  { id: '1h', label: '1 hour' },
  { id: '24h', label: '24 hours' },
];

const NOTE_MAX = 200;

/** A required number that must be above 0 (and at most `max`). */
const badNumber = (v: unknown, max?: number) => {
  const n = Number(v);
  return v === '' || v == null || !(n > 0) || (max != null && n > max);
};

export function AlertBuilder() {
  const prefill = useAlertBuilder();
  if (!prefill) return null;
  return <BuilderForm key={JSON.stringify(prefill)} />;
}

function BuilderForm() {
  const prefill = useAlertBuilder()!;
  const editing = prefill.alert ?? null;
  const instruments = useInstruments();
  const account = useActiveAccount();
  const watchlists = useWatchlists();
  const initialKind: AlertKind = editing?.kind ?? prefill.kind ?? (prefill.symbol ? 'price_above' : 'position_pnl');
  const [kind, setKind] = useState<AlertKind>(initialKind);
  const [symbol, setSymbol] = useState<string | null>(editing?.symbol ?? prefill.symbol ?? instruments[0]?.symbol ?? null);
  const inst = useInstrument(symbol ?? '');
  const ticker = useTicker(symbol ?? '');
  const [params, setParams] = useState<Record<string, any>>(() => editing?.params ?? { ...(DEFAULT_PARAMS[initialKind] ?? {}), ...(prefill.params ?? {}) });
  const [channels, setChannels] = useState<AlertChannel[]>(editing?.channels ?? ['bell', 'push']);
  const [repeat, setRepeat] = useState<AlertRepeat>(editing?.repeat ?? 'once');
  const [expires, setExpires] = useState<string>(editing ? (editing.expiresAt ? 'keep' : 'never') : '30');
  const [note, setNote] = useState(editing?.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pushOn, setPushOn] = useState<boolean | null>(null);
  const scope = scopeOf(kind);
  const calendar = kind === 'event' || kind === 'key_events';

  useEffect(() => {
    void loadWatchlists();
    pushSubscribed().then(setPushOn, () => setPushOn(false));
  }, []);

  // A price alert starts 2 % away from the current price, once: a price the user cleared stays empty.
  useEffect(() => {
    if (editing || params.price !== undefined || !ticker || !inst) return;
    if (kind === 'price_above' || kind === 'price_below') {
      const m = Number(ticker.mark);
      setParams((p) => ({ ...p, price: roundTo(kind === 'price_above' ? m * 1.02 : m * 0.98, inst.priceDecimals) }));
    }
  }, [kind, ticker, inst, editing, params.price]);

  const changeKind = (k: AlertKind) => {
    setKind(k);
    setError(null);
    setTried(false);
    setParams({ ...(DEFAULT_PARAMS[k] ?? {}) });
    if (k === 'daily_summary') setRepeat('daily');
    else if (repeat === 'daily' && k !== 'move' && k !== 'watchlist_move') setRepeat('once');
  };
  const changeScope = (s: Scope) => changeKind(SCOPE_KINDS[s][0]!);
  const set = (k: string, v: unknown) => setParams((p) => ({ ...p, [k]: v }));
  const wlName = watchlists?.find((w) => w.id === params.watchlistId)?.name;

  useEffect(() => {
    if (kind === 'watchlist_move' && !params.watchlistId && watchlists?.[0]) set('watchlistId', watchlists[0].id);
  }, [kind, watchlists, params.watchlistId]);

  // Fields that stop the alert being saved, shown once the user has tried to save.
  const invalid = {
    price: (kind === 'price_above' || kind === 'price_below') && badNumber(params.price),
    pct: (kind === 'move' || kind === 'watchlist_move') && badNumber(params.pct, 100),
    usd: kind === 'position_pnl' && badNumber(params.usd),
    kpct: (kind === 'tp_sl_near' || kind === 'equity_drawdown' || kind === 'margin_level' || kind === 'concentration') && badNumber(params.pct),
    channels: !channels.length,
    keyEvents: kind === 'key_events' && !params.high && !(params.categories ?? []).length,
  };
  const blocked = Object.values(invalid).some(Boolean);
  const show = (k: keyof typeof invalid) => tried && invalid[k];

  // How far the alert price is from now, and whether it would fire straight away.
  const mark = ticker ? Number(ticker.mark) : null;
  const price = Number(params.price);
  const priceHelp =
    (kind === 'price_above' || kind === 'price_below') && mark && price > 0
      ? (kind === 'price_above' ? price <= mark : price >= mark)
        ? { tone: 'warn' as const, text: `The price is already ${kind === 'price_above' ? 'above' : 'below'} this, so the alert would fire straight away.` }
        : { tone: 'muted' as const, text: `Now ${ticker!.mark} · ${price >= mark ? '+' : ''}${((price / mark - 1) * 100).toFixed(2)}% from now` }
      : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (blocked) {
      setTried(true);
      return;
    }
    setBusy(true);
    const body: AlertInput = {
      kind,
      symbol: kind === 'key_events' ? null : scope === 'asset' || kind === 'event' || (scope === 'portfolio' && prefill.symbol && kind !== 'daily_summary') ? symbol : null,
      accountId: scope === 'portfolio' ? (account?.id ?? null) : null,
      params,
      channels,
      repeat,
      note,
      // Calendar alerts end on their own (the release passes) or follow releases until switched off.
      ...(expires === 'keep' || calendar ? {} : { expiresInDays: expires === 'never' ? null : Number(expires) }),
    };
    try {
      if (editing) await updateAlert(editing.id, body);
      else await createAlert(body);
      closeAlertBuilder();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the alert. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const title = kind === 'key_events' ? 'Key economic events' : editing ? 'Edit alert' : scope === 'portfolio' ? 'New portfolio alert' : scope === 'watchlist' ? 'New watchlist alert' : `New alert${symbol ? ` · ${pairLabel(symbol)}` : ''}`;
  const heading = kind === 'key_events' ? 'Key economic events' : editing ? 'Edit alert' : scope === 'portfolio' ? 'New portfolio alert' : scope === 'watchlist' ? 'New watchlist alert' : 'New alert';
  const repeatable = !calendar && kind !== 'daily_summary';
  const guide = kind === 'event' && params.guide ? ECON_GUIDE_BY_SLUG[params.guide] : undefined;

  return (
    <Modal open onClose={closeAlertBuilder} title={title} heading={<span className="text-[22px] font-bold tracking-[-0.01em]">{heading}</span>} width={460}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-5 text-[14px]">
        {!editing && !calendar && (
          <Field label="Alert on">
            <Chips<Scope>
              label="Alert on"
              value={scope}
              onChange={changeScope}
              options={[
                { id: 'asset', label: 'Instrument' },
                { id: 'watchlist', label: 'Watchlist' },
                { id: 'portfolio', label: 'Portfolio', disabled: !account },
              ]}
            />
          </Field>
        )}

        {scope === 'asset' && !editing && !calendar && (
          <Field label="Instrument" htmlFor="alert-symbol">
            <InstrumentPicker
              symbol={symbol}
              onPick={(s) => {
                setSymbol(s);
                setParams((p) => ({ ...p, price: undefined }));
              }}
            />
          </Field>
        )}

        {!calendar && (
          <Field label="Condition" htmlFor="alert-kind">
            <div className="relative">
              <select id="alert-kind" className={cx(inputCls, fieldBorder(false), 'appearance-none pr-9 disabled:opacity-60')} value={kind} disabled={!!editing} onChange={(e) => changeKind(e.target.value as AlertKind)}>
                {SCOPE_KINDS[scope].map((k) => (
                  <option key={k} value={k}>
                    {ALERT_KIND_LABEL[k]}
                  </option>
                ))}
              </select>
              <Icon name="chevron" size={14} className="pointer-events-none absolute top-[14px] right-3 text-muted" />
            </div>
          </Field>
        )}

        {(kind === 'price_above' || kind === 'price_below') && (
          <Field label="Price" htmlFor="alert-price">
            <input id="alert-price" type="number" inputMode="decimal" step="any" min="0" aria-invalid={show('price')} className={cx(inputCls, fieldBorder(show('price')), 'num')} value={params.price ?? ''} onChange={(e) => set('price', e.target.value)} />
            {show('price') ? <Help tone="down">Enter a price above 0</Help> : priceHelp && <Help tone={priceHelp.tone}>{priceHelp.text}</Help>}
          </Field>
        )}
        {kind === 'watchlist_move' && (
          <Field label="Watchlist" htmlFor="alert-watchlist">
            <div className="relative">
              <select id="alert-watchlist" className={cx(inputCls, fieldBorder(false), 'appearance-none pr-9')} value={params.watchlistId ?? ''} onChange={(e) => set('watchlistId', e.target.value)}>
                {(watchlists ?? []).map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name} ({w.symbols.length})
                  </option>
                ))}
              </select>
              <Icon name="chevron" size={14} className="pointer-events-none absolute top-[14px] right-3 text-muted" />
            </div>
          </Field>
        )}
        {(kind === 'move' || kind === 'watchlist_move') && (
          <>
            <Field label="Move" htmlFor="alert-pct">
              <div className="relative">
                <input id="alert-pct" type="number" inputMode="decimal" step="0.1" min="0.1" max="100" aria-invalid={show('pct')} className={cx(inputCls, fieldBorder(show('pct')), 'num pr-9')} value={params.pct ?? ''} onChange={(e) => set('pct', e.target.value)} />
                <span aria-hidden="true" className="pointer-events-none absolute top-[11px] right-3.5 text-muted">%</span>
              </div>
              {show('pct') && <Help tone="down">Enter between 0.1 and 100</Help>}
            </Field>
            <Field label="Within">
              <Chips label="Within" value={params.window} onChange={(v) => set('window', v)} options={WINDOW} />
            </Field>
          </>
        )}
        {kind === 'move' && (
          <Field label="Direction">
            <Chips label="Direction" value={params.direction} onChange={(v) => set('direction', v)} options={[{ id: 'any', label: 'Either way' }, { id: 'up', label: 'Up' }, { id: 'down', label: 'Down' }]} />
          </Field>
        )}
        {kind === 'range_break' && (
          <>
            <Field label="Breaks">
              <Chips label="Breaks" value={params.side} onChange={(v) => set('side', v)} options={[{ id: 'high', label: 'Above the high' }, { id: 'low', label: 'Below the low' }]} />
            </Field>
            <Field label="Of the last">
              <Chips label="Of the last" value={params.window} onChange={(v) => set('window', v)} options={[{ id: '24h', label: '24 hours' }, { id: '7d', label: '7 days' }]} />
            </Field>
          </>
        )}
        {kind === 'event' && (
          <>
            <div className="rounded-lg border border-dashed border-line-strong px-3.5 py-3 text-[13px] text-muted">
              <b className="block text-fg">
                {params.title}
                {params.at && !params.every ? ` · ${new Date(params.at).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
              </b>
              {guide && !guide.generic ? `${guide.short}.` : 'An economic release on the calendar.'}
              {symbol ? ` Usually moves ${pairLabel(symbol)}.` : ''}
            </div>
            {params.series && (
              <Field label="Which releases">
                <Chips label="Which releases" value={params.every ? 'every' : 'one'} onChange={(v) => set('every', v === 'every')} options={[{ id: 'one', label: 'Only this one' }, { id: 'every', label: 'Every release' }]} />
              </Field>
            )}
            <Field label="Remind me">
              <Chips label="Remind me" value={String(params.minutesBefore ?? 15)} onChange={(v) => set('minutesBefore', Number(v))} options={EVENT_REMIND_MINUTES.map((m) => ({ id: String(m), label: `${m} min before` }))} />
            </Field>
            <Field label="Also send">
              <MultiChips label="Also send" value={params.result ? ['result'] : []} onChange={(v) => set('result', v.includes('result'))} options={[{ id: 'result', label: 'The result, actual vs forecast' }]} />
              <Help>Results come in for the releases we have a figure for, usually within a minute.</Help>
            </Field>
          </>
        )}
        {kind === 'key_events' && (
          <>
            <Field label="Which events">
              <MultiChips label="Which events" value={params.high ? ['high'] : []} onChange={(v) => set('high', v.includes('high'))} options={[{ id: 'high', label: 'Every high-impact release' }]} />
            </Field>
            <Field label="Also every release in">
              <MultiChips
                label="Also every release in"
                value={params.categories ?? []}
                onChange={(v) => set('categories', v)}
                options={ECON_CATEGORIES.filter((c) => c.id !== 'holidays' && c.id !== 'other').map((c) => ({ id: c.id, label: c.label }))}
              />
              {show('keyEvents') ? <Help tone="down">Choose high-impact releases or at least one category</Help> : <Help>Medium and high impact releases in these categories, such as US crude oil inventories in Energy.</Help>}
            </Field>
            <Field label="Currencies">
              <MultiChips label="Currencies" value={params.currencies ?? []} onChange={(v) => set('currencies', v)} options={ECON_CURRENCIES.map((c) => ({ id: c, label: c }))} />
              <Help>{(params.currencies ?? []).length ? 'Only releases for these currencies.' : 'None chosen: every currency.'}</Help>
            </Field>
            <Field label="Remind me">
              <Chips label="Remind me" value={String(params.minutesBefore ?? 15)} onChange={(v) => set('minutesBefore', Number(v))} options={EVENT_REMIND_MINUTES.map((m) => ({ id: String(m), label: `${m} min before` }))} />
            </Field>
            <Field label="Also send">
              <MultiChips label="Also send" value={params.result ? ['result'] : []} onChange={(v) => set('result', v.includes('result'))} options={[{ id: 'result', label: 'The result, actual vs forecast' }]} />
            </Field>
          </>
        )}
        {kind === 'position_pnl' && (
          <>
            <Field label="When P&L is">
              <Chips label="When P&L is" value={params.direction} onChange={(v) => set('direction', v)} options={[{ id: 'gain', label: 'Up by' }, { id: 'loss', label: 'Down by' }]} />
            </Field>
            <Field label="Amount" htmlFor="alert-usd">
              <div className="relative">
                <span aria-hidden="true" className="pointer-events-none absolute top-[11px] left-3 text-muted">$</span>
                <input id="alert-usd" type="number" inputMode="decimal" min="1" step="1" aria-invalid={show('usd')} className={cx(inputCls, fieldBorder(show('usd')), 'num pl-7')} value={params.usd ?? ''} onChange={(e) => set('usd', e.target.value)} />
              </div>
              {show('usd') && <Help tone="down">Enter an amount above 0</Help>}
            </Field>
          </>
        )}
        {(kind === 'tp_sl_near' || kind === 'equity_drawdown' || kind === 'margin_level' || kind === 'concentration') && (
          <Field label={kind === 'margin_level' ? 'Margin level' : kind === 'equity_drawdown' ? 'Drawdown' : kind === 'concentration' ? 'Share of open positions' : 'Distance'} htmlFor="alert-kpct">
            <div className="relative">
              <input id="alert-kpct" type="number" inputMode="decimal" step="any" min="0.01" aria-invalid={show('kpct')} className={cx(inputCls, fieldBorder(show('kpct')), 'num pr-9')} value={params.pct ?? ''} onChange={(e) => set('pct', e.target.value)} />
              <span aria-hidden="true" className="pointer-events-none absolute top-[11px] right-3.5 text-muted">%</span>
            </div>
            {show('kpct') && <Help tone="down">Enter a number above 0</Help>}
          </Field>
        )}
        {kind === 'daily_summary' && (
          <Field label="Send at" htmlFor="alert-hour">
            <div className="relative">
              <select id="alert-hour" className={cx(inputCls, fieldBorder(false), 'appearance-none pr-9')} value={params.hourUtc ?? 21} onChange={(e) => set('hourUtc', Number(e.target.value))}>
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}:00 UTC
                  </option>
                ))}
              </select>
              <Icon name="chevron" size={14} className="pointer-events-none absolute top-[14px] right-3 text-muted" />
            </div>
          </Field>
        )}

        <p className="m-0 flex items-start gap-2.5 rounded-lg border border-line bg-raised px-3.5 py-3 leading-[1.45]">
          <Icon name="bell" size={16} className="mt-0.5 shrink-0 text-muted" />
          <span>
            {describe(kind, symbol, params, wlName)}
            {scope === 'portfolio' && account && <> On your {account.kind} account.</>}
          </span>
        </p>

        <Field label="Notify me by">
          <div role="group" aria-label="Notify me by" className="flex flex-wrap gap-2">
            {(['bell', 'push', 'email'] as AlertChannel[]).map((c) => {
              const on = channels.includes(c);
              return (
                <button key={c} type="button" aria-pressed={on} onClick={() => setChannels((cs) => (on ? cs.filter((x) => x !== c) : [...cs, c]))} className={cx(chipCls(on), 'inline-flex items-center gap-2 px-3')}>
                  <span aria-hidden="true" className={cx('grid size-3.5 place-items-center rounded-[4px] border', on ? 'border-fg bg-fg text-bg' : 'border-muted')}>
                    {on && <Icon name="check" size={10} strokeWidth={4} />}
                  </span>
                  {c === 'bell' ? 'In the app' : c === 'push' ? 'Push' : 'Email'}
                </button>
              );
            })}
          </div>
          {show('channels') && <Help tone="down">Pick at least one way to be notified</Help>}
          {channels.includes('push') && pushOn === false && (
            <Help>
              Push is off on this device. Turn it on in{' '}
              <Link to="/account/alerts" onClick={closeAlertBuilder} className="text-fg underline">
                Account → Alerts
              </Link>
              .
            </Help>
          )}
        </Field>

        {repeatable && (
          <Field label="Repeat">
            <Chips<AlertRepeat> label="Repeat" value={repeat} onChange={setRepeat} options={REPEAT} />
            <Help>{REPEAT.find((r) => r.id === repeat)?.hint}</Help>
          </Field>
        )}

        {!calendar && (
          <Field label="Expires">
            <Chips
              label="Expires"
              value={expires}
              onChange={setExpires}
              options={[
                ...(editing?.expiresAt ? [{ id: 'keep', label: `On ${new Date(editing.expiresAt).toLocaleDateString()}` }] : []),
                { id: '7', label: '7 days' },
                { id: '30', label: '30 days' },
                { id: '90', label: '90 days' },
                { id: 'never', label: 'Never' },
              ]}
            />
          </Field>
        )}

        <Field label="Note (optional)" htmlFor="alert-note" aside={note && <span className="num text-[12px] text-muted">{note.length}/{NOTE_MAX}</span>}>
          <input id="alert-note" className={cx(inputCls, fieldBorder(false))} maxLength={NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Take profit here" />
        </Field>

        {error && <p role="alert" className="m-0 rounded-lg bg-down-bg px-3 py-2 text-down">{error}</p>}

        <div className="grid grid-cols-3 gap-2.5">
          <button type="button" onClick={closeAlertBuilder} className="h-11 rounded-lg border border-line-strong font-medium text-fg hover:border-muted">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="col-span-2 flex h-11 items-center justify-center gap-2 rounded-lg bg-fg text-[15px] font-semibold text-bg disabled:cursor-progress disabled:opacity-80">
            {busy && <Spinner />}
            {busy ? 'Saving…' : editing ? 'Save alert' : 'Create alert'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
